// client/src/pages/Account.js
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.js';
import Input from '../components/ui/Input.js';
import Button from '../components/ui/Button.js';
import Modal from '../components/ui/Modal.js';
import authService from '../services/authService.js';
import api from '../services/api.js';

export default function Account() {
  const { user, logout, loadUser } = useAuth();
  const [form, setForm] = useState({
    firstname: user?.firstname || '',
    lastname: user?.lastname || '',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');

  // Subscription & cancellation state
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [refundPreview, setRefundPreview] = useState(null);
  const [cancelOption, setCancelOption] = useState('end'); // 'end' or 'immediate'
  const [reactivating, setReactivating] = useState(false);

  // Usage stats
  const [usage, setUsage] = useState(null);

  // Trial detection
  const isOnTrial = user?.trial_expires_at && new Date(user.trial_expires_at) > new Date() && !user.stripecustomerid;
  const trialExpired = user?.has_used_trial && !user?.trial_expires_at && !user?.tier?.ispaid;

  // Fetch subscription and usage on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (user?.tier?.ispaid) {
          const subData = await api.get('/subscriptions/current');
          setSubscription(subData.subscription);
        }
        const usageData = await api.get('/moonbeams/usage');
        setUsage(usageData);
      } catch (err) {
        console.error('Error fetching account data:', err);
      }
      setLoadingSubscription(false);
    };

    if (user) {
      fetchData();
    } else {
      setLoadingSubscription(false);
    }
  }, [user]);

  // Fetch refund preview when cancel modal opens
  useEffect(() => {
    const fetchRefundPreview = async () => {
      if (showCancel && subscription) {
        try {
          const data = await api.get('/subscriptions/refund-preview');
          setRefundPreview(data);
        } catch (err) {
          console.error('Error fetching refund preview:', err);
        }
      }
    };
    fetchRefundPreview();
  }, [showCancel, subscription]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await authService.updateProfile(form);
      await loadUser();
      setMessage('Profile updated!');
    } catch (err) {
      setMessage(err.data?.error || 'Update failed');
    }
    setSaving(false);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleManageBilling = async () => {
    try {
      const data = await api.post('/subscriptions/portal');
      window.open(data.url, '_blank');
    } catch {
      setMessage('Unable to open billing portal');
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      const immediate = cancelOption === 'immediate';
      const refund = immediate && refundPreview?.eligible;

      const data = await api.post('/subscriptions/cancel', {
        immediate,
        refund,
      });

      setMessage(data.message);
      setShowCancel(false);

      await loadUser();
      const subData = await api.get('/subscriptions/current');
      setSubscription(subData.subscription);

      if (immediate) {
        setSubscription(null);
      }
    } catch (err) {
      setMessage(err.data?.error || 'Failed to cancel subscription');
    }
    setCancelLoading(false);
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      const data = await api.post('/subscriptions/reactivate');
      setMessage(data.message);

      const subData = await api.get('/subscriptions/current');
      setSubscription(subData.subscription);
      await loadUser();
    } catch (err) {
      setMessage(err.data?.error || 'Failed to reactivate subscription');
    }
    setReactivating(false);
  };

  const handleDeleteAccount = async () => {
    try {
      await authService.deleteAccount(deleteConfirm);
      logout();
    } catch (err) {
      setMessage(err.data?.error || 'Failed to delete account');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  if (!user) return null;

  const isAnnual = refundPreview?.interval === 'yearly' || subscription?.interval === 'year';
  const isPendingCancel = subscription?.cancelatperiodend;

  // Shared card classes: deep solid blue in light mode, original translucent in dark mode
  const cardClass = 'bg-royal-900 dark:bg-royal-800/40 border border-royal-700 dark:border-royal-600 rounded-xl';

  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
      <div className="max-w-2xl mx-auto px-4 py-8">

        {/* Dashboard Back Link */}
        <div className="mb-5">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Dashboard
          </Link>
        </div>

        <h1 className="font-display text-3xl text-gold-dark dark:text-gold mb-6">Account</h1>

        {message && (
          <div className="mb-4 p-3 bg-royal-800 border border-gold/30 rounded-lg text-gold text-sm">
            {message}
          </div>
        )}

        {/* Trial Status Banner.
            Light mode uses the same deep navy card treatment as the rest of
            the page so the gold heading and white body text stay readable
            against the cream page background. Dark mode keeps the soft
            gold-tinted accent treatment that already worked there. */}
        {isOnTrial && (
          <div className="bg-royal-900 dark:bg-gold/10 border border-gold/40 dark:border-gold/30 rounded-xl p-5 mb-6">
            <div className="flex items-start gap-3">
              <div className="shrink-0 mt-0.5 text-2xl">✨</div>
              <div className="flex-1">
                <h2 className="text-gold font-display text-lg mb-1">You're on a Free Trial</h2>
                <p className="text-white text-sm mb-1">
                  Enjoying <span className="text-gold font-medium">{user.typename}</span> features until{' '}
                  <span className="text-gold font-medium">
                    {formatDate(user.trial_expires_at)}
                  </span>
                </p>
                <p className="text-royal-100 text-sm mb-3">
                  {(() => {
                    const days = Math.ceil((new Date(user.trial_expires_at) - new Date()) / (1000 * 60 * 60 * 24));
                    return `${days} day${days !== 1 ? 's' : ''} remaining. Subscribe to keep your features after the trial ends.`;
                  })()}
                </p>
                <Link
                  to="/pricing"
                  className="inline-block px-5 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-sm hover:bg-gold-light transition-colors"
                >
                  Subscribe Now
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Trial Expired Notice */}
        {trialExpired && (
          <div className={`${cardClass} p-5 mb-6`}>
            <div className="flex items-start gap-3">
              <div className="shrink-0 mt-0.5 text-2xl">⏰</div>
              <div className="flex-1">
                <h2 className="text-white font-medium text-lg mb-1">Your Free Trial Has Ended</h2>
                <p className="text-royal-100 text-sm mb-3">
                  Subscribe to regain access to premium features like sending cards, scheduling, and more.
                </p>
                <Link
                  to="/pricing"
                  className="inline-block px-5 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-sm hover:bg-gold-light transition-colors"
                >
                  View Plans
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Plan & Subscription Section */}
        <div className={`${cardClass} p-6 mb-6`}>
          <h2 className="text-lg text-white mb-1">
            Plan: {user.typename || 'Free'}
            {isOnTrial && <span className="text-gold text-sm ml-2">(Trial)</span>}
          </h2>
          <p className="text-royal-100 text-sm mb-4">{user.email}</p>

          {/* Subscription Status */}
          {subscription && !isPendingCancel && (
            <div className="mb-4 p-3 bg-royal-900/50 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <span className="text-royal-100">
                  {subscription.interval === 'year' ? 'Annual' : 'Monthly'} subscription
                </span>
                <span className="text-royal-100">Renews {formatDate(subscription.currentperiodend)}</span>
              </div>
            </div>
          )}

          {/* Pending Cancellation Warning */}
          {isPendingCancel && (
            <div className="mb-4 p-4 bg-amber-900/20 border border-amber-600/40 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="shrink-0 mt-0.5">
                  <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-amber-200 font-medium">Your subscription is ending</p>
                  <p className="text-amber-200 text-sm mt-1">
                    Access expires on {formatDate(subscription.currentperiodend)}. You won't be charged again.
                  </p>
                  <button
                    onClick={handleReactivate}
                    disabled={reactivating}
                    className="mt-3 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
                  >
                    {reactivating ? 'Reactivating...' : 'Reactivate Subscription'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            {user.tier?.ispaid && !isOnTrial ? (
              <>
                <Button variant="outline" className="text-sm py-2 px-4" onClick={handleManageBilling}>
                  Manage Billing
                </Button>
                <Link
                  to="/account/change-plan"
                  className="text-gold hover:text-gold-light text-sm transition-colors"
                >
                  Change Plan
                </Link>
                {!isPendingCancel && (
                  <button
                    onClick={() => setShowCancel(true)}
                    className="text-red-400 hover:text-red-300 text-sm transition-colors"
                  >
                    Cancel Subscription
                  </button>
                )}
              </>
            ) : (
              <Link
                to="/pricing"
                className="text-gold hover:text-gold-light text-sm font-medium transition-colors"
              >
                {isOnTrial ? 'Subscribe to keep features →' : 'Upgrade →'}
              </Link>
            )}
          </div>
        </div>

        {/* Usage Stats Section */}
        {usage && (
          <div className={`${cardClass} p-6 mb-6`}>
            <h2 className="text-lg text-white mb-4">
              {usage.deliveries?.isUnlimited ? 'Usage' : 'Usage This Month'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Deliveries */}
              <div className="bg-royal-900/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-royal-100 text-sm">Card Deliveries</span>
                  <span className="text-white text-sm font-medium">
                    {usage.deliveries?.isUnlimited
                      ? `${usage.deliveries.used} sent`
                      : `${usage.deliveries.used} / ${usage.deliveries.limit}`
                    }
                  </span>
                </div>
                {!usage.deliveries?.isUnlimited && (
                  <div className="w-full bg-royal-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        usage.deliveries.used / usage.deliveries.limit > 0.9
                          ? 'bg-red-500'
                          : usage.deliveries.used / usage.deliveries.limit > 0.7
                            ? 'bg-amber-500'
                            : 'bg-gold'
                      }`}
                      style={{ width: `${Math.min(100, (usage.deliveries.used / usage.deliveries.limit) * 100)}%` }}
                    />
                  </div>
                )}
                <p className="text-royal-100 text-sm mt-2">
                  {usage.deliveries?.isUnlimited
                    ? 'Unlimited deliveries'
                    : `${usage.deliveries.remaining} deliveries remaining`
                  }
                </p>
              </div>

              {/* Contacts */}
              <div className="bg-royal-900/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-royal-100 text-sm">Contacts</span>
                  <span className="text-white text-sm font-medium">
                    {usage.contacts?.isUnlimited
                      ? `${usage.contacts.used} saved`
                      : `${usage.contacts.used} / ${usage.contacts.limit}`
                    }
                  </span>
                </div>
                {!usage.contacts?.isUnlimited && (
                  <div className="w-full bg-royal-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        usage.contacts.used / usage.contacts.limit > 0.9
                          ? 'bg-red-500'
                          : usage.contacts.used / usage.contacts.limit > 0.7
                            ? 'bg-amber-500'
                            : 'bg-gold'
                      }`}
                      style={{ width: `${Math.min(100, (usage.contacts.used / usage.contacts.limit) * 100)}%` }}
                    />
                  </div>
                )}
                <p className="text-royal-100 text-sm mt-2">
                  {usage.contacts?.isUnlimited
                    ? 'Unlimited contacts'
                    : `${usage.contacts.remaining} contacts remaining`
                  }
                </p>
              </div>
            </div>
            
            {!usage.deliveries?.isUnlimited && !usage.contacts?.isUnlimited && 
             (usage.deliveries.remaining <= 10 || usage.contacts.remaining <= 5) && (
              <div className="mt-4 p-3 bg-amber-900/20 border border-amber-600/30 rounded-lg">
                <p className="text-amber-200 text-sm">
                  Running low on {usage.deliveries.remaining <= 10 ? 'deliveries' : 'contacts'}?{' '}
                  <Link to="/pricing" className="text-gold hover:text-gold-light underline">
                    Upgrade your plan
                  </Link>
                </p>
              </div>
            )}
          </div>
        )}

        {/* Profile Form */}
        <form onSubmit={handleUpdate} className={`${cardClass} p-6 mb-6`}>
          <h2 className="text-lg text-white mb-4">Profile</h2>
          <div className="grid grid-cols-2 gap-4">
            <Input label="First Name" value={form.firstname} onChange={(e) => setForm({ ...form, firstname: e.target.value })} />
            <Input label="Last Name" value={form.lastname} onChange={(e) => setForm({ ...form, lastname: e.target.value })} />
          </div>
          <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Update Profile'}</Button>
        </form>

        {/* Legal Links */}
        <div className="flex items-center gap-4 mt-6 mb-4">
          <Link to="/privacy" className="text-royal-500 dark:text-royal-200 text-sm hover:text-gold-dark dark:hover:text-gold transition-colors">
            Privacy Policy
          </Link>
          <span className="text-royal-300 dark:text-royal-600 text-sm">·</span>
          <Link to="/terms" className="text-royal-500 dark:text-royal-200 text-sm hover:text-gold-dark dark:hover:text-gold transition-colors">
            Terms of Service
          </Link>
        </div>

        {/* Delete Account */}
        <div className="border-t border-royal-300/30 dark:border-royal-700 pt-6">
          <button onClick={() => setShowDelete(true)} className="text-red-500 dark:text-red-400 hover:text-red-400 dark:hover:text-red-300 text-sm">
            Delete My Account
          </button>
        </div>

        {/* Cancel Subscription Modal */}
        <Modal isOpen={showCancel} onClose={() => setShowCancel(false)} title="Cancel Subscription">
          {refundPreview ? (
            <div className="space-y-4">
              {/* Monthly User - No Refund */}
              {!refundPreview.eligible && (
                <>
                  <p className="text-royal-100">
                    Your monthly subscription will remain active until <strong className="text-white">{formatDate(refundPreview.periodEnd)}</strong>.
                  </p>
                  <p className="text-royal-100 text-sm">
                    Monthly subscriptions are not eligible for refunds.
                  </p>

                  <div className="bg-royal-900/50 p-4 rounded-lg">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="cancelOption"
                        checked={cancelOption === 'end'}
                        onChange={() => setCancelOption('end')}
                        className="mt-1"
                      />
                      <div>
                        <p className="text-white font-medium">Cancel at end of billing period</p>
                        <p className="text-royal-100 text-sm">
                          Keep access until {formatDate(refundPreview.periodEnd)}
                        </p>
                      </div>
                    </label>
                  </div>
                </>
              )}

              {/* Annual User - Refund Eligible */}
              {refundPreview.eligible && (
                <>
                  <p className="text-royal-100">
                    You have <strong className="text-white">{refundPreview.daysRemaining} days</strong> remaining on your annual subscription.
                  </p>

                  <div className="space-y-3">
                    {/* Option 1: Cancel at end */}
                    <div className="bg-royal-900/50 p-4 rounded-lg">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="cancelOption"
                          checked={cancelOption === 'end'}
                          onChange={() => setCancelOption('end')}
                          className="mt-1"
                        />
                        <div>
                          <p className="text-white font-medium">Cancel at end of billing period</p>
                          <p className="text-royal-100 text-sm">
                            Keep access until {formatDate(refundPreview.periodEnd)} — no refund
                          </p>
                        </div>
                      </label>
                    </div>

                    {/* Option 2: Cancel immediately with refund */}
                    <div className="bg-royal-900/50 p-4 rounded-lg border border-green-700/50">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="cancelOption"
                          checked={cancelOption === 'immediate'}
                          onChange={() => setCancelOption('immediate')}
                          className="mt-1"
                        />
                        <div>
                          <p className="text-white font-medium">Cancel now with prorated refund</p>
                          <p className="text-green-400 text-sm font-medium">
                            Receive {formatCurrency(refundPreview.refundAmount)} refund
                          </p>
                          <p className="text-royal-100 text-sm mt-1">
                            Access ends immediately
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                </>
              )}

              <div className="flex gap-3 mt-6">
                <Button
                  variant="danger"
                  onClick={handleCancelSubscription}
                  disabled={cancelLoading}
                >
                  {cancelLoading ? 'Processing...' : 'Confirm Cancellation'}
                </Button>
                <Button variant="ghost" onClick={() => setShowCancel(false)}>
                  Keep Subscription
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
            </div>
          )}
        </Modal>

        {/* Delete Account Modal */}
        <Modal isOpen={showDelete} onClose={() => setShowDelete(false)} title="Delete Account">
          <p className="text-royal-100 mb-4">
            Your account will be permanently deleted after 30 days. Type <strong className="text-white">DELETE</strong> to confirm.
          </p>
          <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder="Type DELETE" />
          <div className="flex gap-3 mt-4">
            <Button variant="danger" onClick={handleDeleteAccount} disabled={deleteConfirm !== 'DELETE'}>
              Delete Account
            </Button>
            <Button variant="ghost" onClick={() => setShowDelete(false)}>Cancel</Button>
          </div>
        </Modal>
      </div>
    </div>
  );
}